import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Environment, Filter, TimePrecision } from '@playq/octopus-common';
import { ClusterizationRunSort, ClusterizationRunsResponse, ClusterizationTaskId } from '@playq/services-beetle';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { services2 } from '/api';
import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { timeUnitPrecisionMillisecondValues } from '/constants';

export const queryRunsKeys: unknown[] = ['run', 'query'];

export const useQueryRuns = (
  id: ClusterizationTaskId,
  iterator: OffsetLimit,
  sortBy: ClusterizationRunSort[],
  filterBy: {
    [key: string]: Filter;
  },
  env: Environment,
  options?: IUseQueryOptionsExtended<ClusterizationRunsResponse | undefined, GenericFailure | Error>
) => {
  const { mutate, refetch, ...res } = useEitherQuery(
    queryRunsKeys.concat(id, iterator, sortBy, filterBy, env),
    () => {
      return services2.audienceRequestsClusterizationService.queryRuns(id, iterator, sortBy, filterBy, env);
    },
    {
      placeholderData: keepPreviousData,
      staleTime: timeUnitPrecisionMillisecondValues[TimePrecision.Minute] * 5,
      gcTime: timeUnitPrecisionMillisecondValues[TimePrecision.Minute] * 6,
      ...options,
    }
  );

  const runs = useMemo(() => res.data?.requests || [], [res.data]);
  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  const keys = useMemo(
    () => queryRunsKeys.concat(id, iterator, sortBy, filterBy, env),
    [id, iterator, env, sortBy, filterBy]
  );
  const { nextKeys } = useQueryPrefetch({
    keys,
    enabled: res.isSuccess && !res.isError,
    total,
    args: [id, iterator, sortBy, filterBy, env],
    serviceMethod: services2.audienceRequestsClusterizationService.queryRuns.bind(
      services2.audienceRequestsClusterizationService
    ),
  });

  return { ...res, queryRuns: { runs, total }, refetch, nextKeys };
};
